// 報告錯誤模型（報告改版工單 17）。純函式、不 import 任何模組（S2：tests/report-errors.test.mjs
// 「error classify」「report code」「retry plan」「client error payload」）。
//
// 詞彙（CONTEXT.md「錯誤」）：
//   - 錯誤類別（errorCode）：後端報告端點回應的穩定分類（五種）＋前端自生的 CLIENT_DECRYPT／CLIENT_RENDER。
//   - 錯誤畫面類別（SCREEN）：前端給使用者看的分類，由錯誤類別與 HTTP status 對照而來。
//   - 回報碼：<報告編號末 6 碼>-<類別段>-<UTC 時間 YYYYMMDDTHHmmSSZ>-<requestId 前 8 碼｜NOREQ>。
//     類別段＝錯誤類別；沒有 errorCode 時為 NETWORK／RATE_LIMITED／HTTP<status>（reportCodeCategory）。
//   - 報告能否顯示（reportReadiness）：頁殼與新舊渲染器共用這一個判斷（工單 17 站5）。
// 對照表的唯一來源＝docs/REPORT_DESIGN_SPEC.md「整頁狀態畫面」；重試預算用完後停在該類別的最終畫面
// （2026-10-04 使用者裁決：不轉系統錯誤、保留手動重試）。
//
// 「結果」（outcome）是 store 每次請求結束後記下的純資料（stores/modules/report.js 的 outcome；不含報告內容）：
//   成功：{ ok: true, status: 200, requestId, isFinish, executionStatus }——isFinish 缺鍵（V1／舊資料）記為 null＝視為已完成。
//   失敗：{ ok: false, status, errorCode, requestId, executionStatus, createdAt, clientCode, network }。

export const ERROR_CODE = Object.freeze({
  REPORT_NOT_FOUND: 'REPORT_NOT_FOUND',
  REPORT_NOT_READY: 'REPORT_NOT_READY',
  LINK_INVALID: 'LINK_INVALID',
  FEATURE_NOT_ENABLED: 'FEATURE_NOT_ENABLED',
  INTERNAL: 'INTERNAL',
  // 前端自生
  CLIENT_DECRYPT: 'CLIENT_DECRYPT',
  CLIENT_RENDER: 'CLIENT_RENDER',
})

// 沒有後端 errorCode 時，回報碼的類別段（工單 17 第 4 點；不是錯誤類別，CONTEXT.md「回報碼」）
export const NETWORK_CODE = 'NETWORK'
export const RATE_LIMITED_CODE = 'RATE_LIMITED'
export const httpCode = status => `HTTP${status}`

// 錯誤畫面類別（CONTEXT.md「錯誤畫面類別」；也是 i18n 鍵 report.error.screen.<screen>.* 的段名）
export const SCREEN = Object.freeze({
  CONNECTION: 'connection', // 連線問題（網路錯、429）
  LINK_INVALID: 'linkInvalid', // 連結失效（401）
  NOT_FOUND: 'notFound', // 找不到報告
  NOT_STARTED: 'notStarted', // 尚未開始量測（executionStatus=pending；工單 60）
  IN_PROGRESS: 'inProgress', // 量測進行中（in_progress 且 isFinish 不是 true、V1 isFinish=false；工單 60）
  GENERATING: 'generating', // 報告產生中（量完到報告可顯示之間、REPORT_NOT_READY 未附 executionStatus，見 reportReadiness）
  ABORTED: 'aborted', // 已中止（executionStatus=aborted）
  NOT_ENABLED: 'notEnabled', // 未開通（FEATURE_NOT_ENABLED）
  SYSTEM: 'system', // 系統錯誤（INTERNAL、CLIENT_DECRYPT、無法歸類者、渲染器 chunk 載入失敗）
  BLOCK_RENDER: 'blockRender', // 區塊錯誤卡（CLIENT_RENDER；只換掉一個區塊）
  // 區塊層級的降級態（CONTEXT.md：不是整頁類別）：次要請求 /aiAnalysis 持續 REPORT_NOT_READY＝「AI 分析暫無結果」
  NO_RESULT: 'noResult',
})

// 報告能否顯示（reportReadiness 的回傳值；不能顯示時＝錯誤畫面類別同名）
export const READINESS = Object.freeze({
  READY: 'ready',
  NOT_STARTED: SCREEN.NOT_STARTED,
  IN_PROGRESS: SCREEN.IN_PROGRESS,
  GENERATING: SCREEN.GENERATING,
  ABORTED: SCREEN.ABORTED,
})
const EXECUTION_ABORTED = 'aborted'
const EXECUTION_PENDING = 'pending'
const EXECUTION_IN_PROGRESS = 'in_progress'
const EXECUTION_COMPLETED = 'completed'

/** executionStatus（200 的 base 或 400 body）＋isFinish → 未完成的類別；工單 60 判斷表的前四列，都不成立回 null。 */
function unfinishedReadiness (status, isFinish) {
  if (status === EXECUTION_ABORTED) return READINESS.ABORTED
  if (status === EXECUTION_PENDING) return READINESS.NOT_STARTED
  if (status === EXECUTION_IN_PROGRESS && isFinish !== true) return READINESS.IN_PROGRESS
  if (!status && isFinish === false) return READINESS.IN_PROGRESS
  return null
}

// 自動重試（docs/REPORT_DESIGN_SPEC.md「整頁狀態畫面」；spec Q35；工單 17 站5 改退避）：
// 第 n 次重試前等 RETRY_INTERVALS_MS[n]（3→5→8→13→20→30 秒，之後固定 30 秒），但不超過預算剩下的時間；
// 剩餘預算不到序列第一個間隔（3 秒）就不再排，直接停在該類別的最終畫面（2026-10-04 主線裁定：放不進預算就用完）。
// 時間點：找不到 3、8、16、29 秒（29 秒後剩 1 秒＝用完）；產生中 3、8、16、29、49、79、109、120 秒。
// 尚未開始量測／量測進行中（工單 60，使用者 2026-10-20「重試選 a」）：不退避、每 30 秒查一次，30 分鐘後停在最終畫面。
export const RETRY_INTERVALS_MS = Object.freeze([3000, 5000, 8000, 13000, 20000, 30000])
const MEASURING_INTERVALS_MS = Object.freeze([30000])
const MEASURING_BUDGET_MS = 30 * 60 * 1000
export const RETRY_BUDGET_MS = Object.freeze({
  [SCREEN.NOT_FOUND]: 30000,
  [SCREEN.GENERATING]: 120000,
  [SCREEN.NO_RESULT]: 120000,
  [SCREEN.NOT_STARTED]: MEASURING_BUDGET_MS,
  [SCREEN.IN_PROGRESS]: MEASURING_BUDGET_MS,
})
const RETRY_INTERVALS_FOR = Object.freeze({
  [SCREEN.NOT_STARTED]: MEASURING_INTERVALS_MS,
  [SCREEN.IN_PROGRESS]: MEASURING_INTERVALS_MS,
})

/** 第 index 次（0 起算）自動重試前的等待；超出序列固定最後一個值（預設序列＝30 秒）。 */
export function retryInterval (index, intervals) {
  const seq = Array.isArray(intervals) && intervals.length ? intervals : RETRY_INTERVALS_MS
  const i = Math.max(0, Math.floor(Number(index) || 0))
  return seq[Math.min(i, seq.length - 1)]
}

const isObject = v => v !== null && typeof v === 'object' && !Array.isArray(v)
const strOrNull = v => (typeof v === 'string' && v.length > 0 ? v : null)
const numOrNull = v => (typeof v === 'number' && Number.isFinite(v) ? v : null)

/**
 * 失敗結果（純資料）。body 只取契約欄位（errorCode／requestId／executionStatus／createdAt），其餘一律不留——
 * 回應裡的任何報告內容都不進 UI 狀態、也不會被上報。
 */
export function failureOutcome ({ status = null, body = null, network = false, clientCode = null, requestId = null } = {}) {
  const b = isObject(body) ? body : {}
  return {
    ok: false,
    status: numOrNull(status),
    errorCode: strOrNull(b.errorCode),
    requestId: strOrNull(requestId) || strOrNull(b.requestId),
    executionStatus: strOrNull(b.executionStatus),
    createdAt: numOrNull(b.createdAt),
    clientCode: strOrNull(clientCode),
    network: network === true,
  }
}

/** 成功結果（純資料）：只留判斷「產生中／已中止」需要的旗標與 requestId。 */
export function successOutcome (body, { requestId = null } = {}) {
  const b = isObject(body) ? body : {}
  const base = isObject(b.base) ? b.base : {}
  return {
    ok: true,
    status: 200,
    requestId: strOrNull(requestId) || strOrNull(b.requestId),
    // 缺鍵＝null（V1 報告或舊資料）：視為已完成，不得誤判成「產生中」
    isFinish: typeof base.isFinish === 'boolean' ? base.isFinish : null,
    executionStatus: strOrNull(base.executionStatus),
  }
}

/**
 * 報告能否顯示——頁殼（主報告 200 時）與新舊渲染器共用這一個判斷（工單 17 站5，取代各自的寫法）。
 * 由上而下取第一個成立的（工單 60 判斷表，使用者 2026-10-20 裁定；新舊渲染器的保底路徑只看是不是 READY）：
 *   1. executionStatus=aborted → 已中止
 *   2. executionStatus=pending → 尚未開始量測
 *   3. executionStatus=in_progress 且 isFinish 不是 true → 量測進行中
 *   4. 無 executionStatus（V1）且 isFinish=false → 量測進行中
 *   5. isFinish=false（completed 但 IsFinish 還沒寫回）→ 產生中；V2 且不是 completed（isFinish=true 但 ExecutionStatus 還沒寫
 *      completed——後端兩次寫入分開）→ 產生中。不認得的 executionStatus 也歸這裡（保守：不顯示未完成的報告）
 *   6. 其餘可以顯示——含 V1 的 isFinish 缺鍵（null／undefined：V1 報告或舊資料視為已完成）
 * @param {{ isFinish?: boolean|null, executionStatus?: string|null }} base
 */
export function reportReadiness (base) {
  const b = isObject(base) ? base : {}
  const status = strOrNull(b.executionStatus)
  const unfinished = unfinishedReadiness(status, b.isFinish)
  if (unfinished) return unfinished
  if (b.isFinish === false) return READINESS.GENERATING
  if (status && status !== EXECUTION_COMPLETED) return READINESS.GENERATING
  return READINESS.READY
}

/**
 * 回報碼的類別段（≠ 錯誤類別）：後端 errorCode 優先；無 errorCode 時 NETWORK／RATE_LIMITED／HTTP<status>；
 * 前端自生用自己的；200 但未完成／已中止為 REPORT_NOT_READY。前端無法歸類的例外為 INTERNAL（2026-10-04 裁決接受）。
 */
export function reportCodeCategory (outcome) {
  const o = isObject(outcome) ? outcome : {}
  if (o.clientCode) return o.clientCode
  if (o.ok) return ERROR_CODE.REPORT_NOT_READY // 200 但未完成／已中止（設計規格對照表 ※1、※3）
  if (o.network) return NETWORK_CODE
  if (o.errorCode) return o.errorCode
  if (o.status === 429) return RATE_LIMITED_CODE
  if (typeof o.status === 'number') return httpCode(o.status)
  return ERROR_CODE.INTERNAL
}

/**
 * 結果 → 錯誤畫面類別。成功且可以顯示報告時回 null。
 * @param {object|null} outcome  successOutcome／failureOutcome 的結果；null（沒有發過請求）＝null
 * @param {object} [opts]
 * @param {boolean} [opts.checkFinish=true]  200 時是否檢查 isFinish／executionStatus（只有主報告要檢查）
 * @param {boolean} [opts.secondary=false]   次要請求（/aiAnalysis 等）：REPORT_NOT_READY＝「暫無結果」、
 *                                           FEATURE_NOT_ENABLED＝未開通，兩者都只影響該區塊（block: true）
 * @returns {{ screen: string, category: string, block: boolean } | null}  category＝回報碼類別段
 */
export function classifyOutcome (outcome, { checkFinish = true, secondary = false } = {}) {
  if (!isObject(outcome)) return null
  const category = reportCodeCategory(outcome)
  const verdict = (screen, block = false) => ({ screen, category, block })

  if (outcome.ok) {
    if (!checkFinish) return null
    const readiness = reportReadiness(outcome)
    return readiness === READINESS.READY ? null : verdict(readiness) // 不能顯示時 readiness＝錯誤畫面類別
  }
  if (outcome.clientCode === ERROR_CODE.CLIENT_RENDER) return verdict(SCREEN.BLOCK_RENDER, true)
  if (outcome.clientCode === ERROR_CODE.CLIENT_DECRYPT) return verdict(SCREEN.SYSTEM)
  if (outcome.network) return verdict(SCREEN.CONNECTION)

  switch (outcome.errorCode) {
    case ERROR_CODE.REPORT_NOT_READY: {
      if (outcome.executionStatus === EXECUTION_ABORTED) return verdict(SCREEN.ABORTED)
      if (secondary) return verdict(SCREEN.NO_RESULT, true)
      // 400 body 沒有 isFinish＝「isFinish 不是 true」；未附 executionStatus（或 completed）＝產生中（工單 60）
      return verdict(unfinishedReadiness(outcome.executionStatus, null) || SCREEN.GENERATING)
    }
    case ERROR_CODE.REPORT_NOT_FOUND:
      return verdict(SCREEN.NOT_FOUND)
    case ERROR_CODE.LINK_INVALID:
      return verdict(SCREEN.LINK_INVALID)
    case ERROR_CODE.FEATURE_NOT_ENABLED:
      return verdict(SCREEN.NOT_ENABLED, secondary)
    case ERROR_CODE.INTERNAL:
      return verdict(SCREEN.SYSTEM)
    case null:
    case undefined:
      break
    default:
      return verdict(SCREEN.SYSTEM) // 未來新增、前端還不認得的 errorCode：照實帶進回報碼，畫面歸系統錯誤
  }
  // 舊後端（尚未帶 errorCode）：依 HTTP status 盡力歸類，其餘歸系統錯誤
  if (outcome.status === 429) return verdict(SCREEN.CONNECTION)
  if (outcome.status === 401) return verdict(SCREEN.LINK_INVALID)
  return verdict(SCREEN.SYSTEM)
}

/** 錯誤畫面類別 → 自動重試計畫。budgetMs 0＝不自動重試（仍可手動）；intervalsMs＝退避序列。 */
export function retryPlan (verdict) {
  const screen = verdict && verdict.screen
  const budgetMs = (screen && RETRY_BUDGET_MS[screen]) || 0
  return { budgetMs, intervalsMs: budgetMs > 0 ? RETRY_INTERVALS_FOR[screen] || RETRY_INTERVALS_MS : [] }
}

/**
 * 一次失敗之後要不要再自動重試。startedAt＝進入這個錯誤畫面類別的時間（同類別連續失敗沿用）；
 * retryIndex＝這個類別內已經自動重試過幾次（決定退避間隔）。間隔序列＝plan.intervalsMs。
 * 預算剩餘 < 序列第一個間隔（退避 3 秒；量測中 30 秒）＝用完（exhausted），停在該類別的最終畫面；否則等 min(間隔, 預算剩餘) 再試。
 */
export function nextRetry ({ plan, startedAt, now, retryIndex = 0 }) {
  if (!plan || !(plan.budgetMs > 0)) return { retry: false, exhausted: false, delayMs: 0 }
  const remainingMs = plan.budgetMs - Math.max(0, now - startedAt)
  if (remainingMs < retryInterval(0, plan.intervalsMs)) return { retry: false, exhausted: true, delayMs: 0 }
  return { retry: true, exhausted: false, delayMs: Math.min(retryInterval(retryIndex, plan.intervalsMs), remainingMs) }
}

/**
 * 暫時性失敗（網路錯、HTTP 5xx）：自動重試途中遇到時，在剩餘預算內照常重試、畫面維持原類別（工單 17 站5）。
 * 429 不算（限流：停下來轉連線問題，不再消耗額度）。
 */
export function isTransientFailure (outcome) {
  if (!isObject(outcome) || outcome.ok) return false
  if (outcome.clientCode) return false
  return outcome.network === true || (typeof outcome.status === 'number' && outcome.status >= 500)
}

const pad2 = n => String(n).padStart(2, '0')

/** Date／毫秒 → UTC「YYYYMMDDTHHmmSSZ」；無效時間回 null。 */
export function utcStamp (at) {
  const d = at instanceof Date ? at : new Date(at)
  if (Number.isNaN(d.getTime())) return null
  return `${d.getUTCFullYear()}${pad2(d.getUTCMonth() + 1)}${pad2(d.getUTCDate())}T${pad2(d.getUTCHours())}${pad2(d.getUTCMinutes())}${pad2(d.getUTCSeconds())}Z`
}

export const NO_REQUEST_ID = 'NOREQ'

/**
 * 回報碼：<報告編號末 6 碼>-<類別段>-<UTC 時間>-<requestId 前 8 碼>；沒有 requestId 時最後一段為 NOREQ。
 * 例：報告 1ad755c1-…-e91d3f9e9e4d、REPORT_NOT_FOUND、2026-10-01T03:04:05Z、requestId 0a1b2c3d-…
 *     → 9e9e4d-REPORT_NOT_FOUND-20261001T030405Z-0a1b2c3d
 * 特殊值（docs/FEATURE_MAP.md「報告錯誤畫面」）：沒有報告編號＝NOID、時間無效＝00000000T000000Z。
 */
export function reportCode ({ reportId, category, at, requestId } = {}) {
  const tail = String(reportId || '').slice(-6) || 'NOID'
  const segment = String(category || ERROR_CODE.INTERNAL).replace(/[^A-Za-z0-9_]/g, '') || ERROR_CODE.INTERNAL
  const time = utcStamp(at === undefined ? Date.now() : at) || '00000000T000000Z'
  const req = strOrNull(requestId) ? requestId.slice(0, 8) : NO_REQUEST_ID
  return `${tail}-${segment}-${time}-${req}`
}

/**
 * 從錯誤狀態快照組回報碼（工單 17 B7：整頁錯誤畫面、推薦頁未開通、CLIENT_RENDER 錯誤卡共用，不再各自湊四個參數）。
 * snapshot＝retry-controller 的狀態快照形狀 { verdict: { category }, failedAt, outcome: { requestId } }；
 * 區塊錯誤卡沒有控制器，自組同形物件（category＝CLIENT_RENDER、outcome.requestId＝主報告的 requestId）。
 * failedAt 缺＝現在（同 reportCode 的 at）。
 */
export function reportCodeFor (snapshot, reportId) {
  const s = isObject(snapshot) ? snapshot : {}
  const verdict = isObject(s.verdict) ? s.verdict : {}
  const outcome = isObject(s.outcome) ? s.outcome : {}
  return reportCode({ reportId, category: verdict.category, at: s.failedAt, requestId: outcome.requestId })
}

/**
 * 整頁錯誤畫面要不要附回報碼框與聯絡窗口行（兩者同在 ReportCodeBox；工單 60 站5，使用者 2026-10-20 裁決）。
 * 「尚未開始量測」「量測進行中」是正常等候、不是出事：重試中（非最終）不顯示，30 分鐘後的最終畫面才顯示；其他類別不論階段都顯示。
 * phase＝ReportErrorScreen 的 retrying／final／none（none＝沒有自動重試、也沒用完預算）。
 */
export function showsReportCode (screen, phase) {
  if (screen === SCREEN.NOT_STARTED || screen === SCREEN.IN_PROGRESS) return phase === 'final'
  return true
}

/** 毫秒 → 「m:ss」（重試進度文字用；負值視為 0）。 */
export function formatDuration (ms) {
  const total = Math.max(0, Math.floor((Number(ms) || 0) / 1000))
  return `${Math.floor(total / 60)}:${pad2(total % 60)}`
}

// 前端錯誤上報（POST /front/v1/report/:reportId/client-error；後端工單 10）
export const CLIENT_ERROR_CODES = Object.freeze([ERROR_CODE.CLIENT_RENDER, ERROR_CODE.CLIENT_DECRYPT])
const MESSAGE_MAX = 500
const ident = (v, max) => (typeof v === 'string' ? v.replace(/[^A-Za-z0-9_.:-]/g, '').slice(0, max) : '')

/**
 * 上報 body（白名單）：{ errorCode, blockId?, message?, clientRequestId?, appVersion? }。
 * message 只放例外「類型」（Error.name）與區塊 id／階段——**不放例外訊息與報告內容**（訊息可能夾帶病患資料）。
 * errorCode 不是 CLIENT_RENDER／CLIENT_DECRYPT 時回 null（不上報）。
 */
export function clientErrorPayload ({ errorCode, blockId, stage, error, clientRequestId, appVersion } = {}) {
  if (!CLIENT_ERROR_CODES.includes(errorCode)) return null
  const payload = { errorCode }
  const block = ident(blockId, 100)
  if (block) payload.blockId = block
  const type = ident(error && error.name, 60) || 'Error'
  const parts = [type]
  if (block) parts.push(`block=${block}`)
  const st = ident(stage, 20)
  if (st) parts.push(`stage=${st}`)
  payload.message = parts.join(' ').slice(0, MESSAGE_MAX)
  const req = ident(clientRequestId, 100)
  if (req) payload.clientRequestId = req
  const version = ident(appVersion, 64)
  if (version) payload.appVersion = version
  return payload
}

/** 上報去重鍵：同一頁面載入內，同報告＋同 blockId＋同 errorCode 只上報一次（與報告 GET 共用限流額度）。 */
export function clientErrorKey ({ reportId, blockId, errorCode } = {}) {
  return `${reportId || ''}\u0000${blockId || ''}\u0000${errorCode || ''}`
}
